import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'sabia.theme';

export const THEMES = [
  {
    id: 'terminal',
    label: 'Terminal',
    description: 'Neon brutalism. Sharp edges, dark base.',
  },
  {
    id: 'bookish',
    label: 'Bookish',
    description: 'Warm parchment. Serif, soft rounded.',
  },
  {
    id: 'kawaii',
    label: 'Kawaii',
    description: 'Pastel and playful. Rounded, friendly.',
  },
  {
    id: 'corporate',
    label: 'Corporate',
    description: 'Beige walls. Stock photo. Synergy.',
  },
];

const VALID = new Set(THEMES.map((t) => t.id));
const DEFAULT = 'terminal';

function readStored() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && VALID.has(v) ? v : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const setTheme = useCallback((id) => {
    if (!VALID.has(id)) return;
    setThemeState(id);
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, themes: THEMES }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
